import * as React from "react";
import "./multiSelectList.scss";
import ButtonSecondary from "../../atoms/Buttons/ButtonSecondary";
import Checkbox from "../../atoms/Checkbox/Checkbox";

interface IProps<T> {
	/**
	 * List of items.
	 */
	items: T[];
	/**
	 * Converts item to item label.
	 * @param {T} item
	 * @return {string}
	 */
	itemToLabel: (item: T) => string;
	/**
	 * Converts item to item id.
	 * @param {T} item
	 * @return {string}
	 */
	itemToId: (item: T) => string;
	/**
	 * Indicates if item is disabled.
	 * @param {T} item
	 * @return {boolean}
	 */
	itemDisabled?: (item: T) => boolean;
	/**
	 * On select callback.
	 * @param {string} id
	 */
	onUpdate?: (item: T[]) => void;
	/**
	 * Selected item id.
	 */
	selectedItemIds?: string[];
	/**
	 * Indicates if onUpdate method is called on every onChange event and update button is not rendered.
	 */
	updateOnChange?: boolean;
	/**
	 * Optional component displayed between the input and the list
	 */
	componentBetweenInputAndList?: JSX.Element
}

interface State<T> {
	/**
	 * Selected items ids holder
	 */
	selectedItemIds?: string[];
}

export default class MultiSelectList<T> extends React.PureComponent<IProps<T>, State<T>> {

	constructor(props: IProps<T>) {
		super(props);
		const selectedItemIds = this.props.selectedItemIds || [];
		this.state = {selectedItemIds};
	}

	componentWillReceiveProps(newProps: IProps<T>) {
		const selectedItemIds = newProps.selectedItemIds || [];
		this.setState({selectedItemIds});
	}

	render() {
		return (
			<React.Fragment>
				<div className={"m-multiselect-dropdown-container"}>
					{this.props.componentBetweenInputAndList}
					<ul className={"m-multiselect-dropdown"}>
						<li key={-1}>
							<Checkbox checked={this.areAllSelected()} label={"All"} onChange={() => this.onAllChange()}/>
						</li>
						{this.props.items.map((item, index) => {
							const itemSelected = this.isItemSelected(item);
							const itemDisabled = this.isItemDisabled(item);
							return (
								<li key={index}>
									<Checkbox checked={itemSelected} disabled={itemDisabled} label={this.props.itemToLabel(item)}
											  onChange={() => this.onChange(item)}/>
								</li>
							);
						})}
					</ul>
					{!this.props.updateOnChange &&
					<div className={"m-multiselect-dropdown-update-container"}>
						<ButtonSecondary label={"Update"} onClick={() => this.onUpdate()}/>
					</div>}
				</div>
			</React.Fragment>
		);
	}

	/**
	 * On all checkbox change
	 */
	onAllChange() {
		let selectedItemIds: string[];
		if (this.areAllSelected()) {
			selectedItemIds = [];
		}
		else {
			selectedItemIds = this.props.items.map(item => this.props.itemToId(item));
		}
		this.setState({selectedItemIds}, () => this.props.updateOnChange && this.onUpdate());
	}

	/**
	 * On select callback.
	 * @param {T} item
	 */
	onChange(item: T) {
		const id = this.props.itemToId(item);
		let selectedItemIds: string[];
		if (!this.isItemDisabled(item)) {
			if (this.isItemSelected(item)) {
				selectedItemIds = [...this.state.selectedItemIds.filter(i => i !== id)];
			}
			else {
				selectedItemIds = [...this.state.selectedItemIds.concat(id)];
			}
			this.setState({selectedItemIds}, () => this.props.updateOnChange && this.onUpdate());
		}
	}

	/**
	 * Indicates if item is selected.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemSelected(item: T) {
		return this.state.selectedItemIds.indexOf(this.props.itemToId(item)) > -1;
	}

	/**
	 * Indicates if item is disabled.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemDisabled(item: T) {
		return this.props.itemDisabled && this.props.itemDisabled(item);
	}

	/**
	 * On update button click
	 */
	onUpdate() {
		let items: T[] = [];
		if (this.areAllSelected()) {
			items = this.props.items.filter(i => !this.isItemDisabled(i))
		}
		else {
			this.props.items.forEach(item => {
				if (this.isItemSelected(item)) {
					items.push(item);
				}
			})
		}

		this.props.onUpdate(items);
	}

	areAllSelected() {
		return this.props.items.length === this.state.selectedItemIds.length
	}
}